import { useEffect, useState } from "react";
import { Button, ProgressBar } from "react-bootstrap";
import { Playlist } from "../utils/types";
import styles from "./Analyse.module.css";
import AnalyseMapView from "./AnalyseMapView";
import AnalyseTimeline from "./AnalyseTimeline";
import { DamageData, FrameData, GrenadeData, PlayerFrameData, RoundData, Side, WeaponFireData, KillData } from "../utils/types";
import { interpolateBombFramesLinear, interpolatePlayerFramesLinear, interpolateProjectileFramesLinear, world_to_screen } from "../utils/math";
import { getShortPlayerName, team_names_equal } from "../utils/teams";
import MultiSelectButtonGroup from "./MultiSelectButtonGroup";
import TeamIcon from "./TeamIcon";
import { CachedProjectiles, getExplosionTick, isProjectileInRange, Projectile, ProjectileInfo } from "../utils/projectile";
import { FireInfo, SmokeInfo } from "./MapView";
import AnalyseHeatMapView from "./AnalyseHeatMapView";
import RangeSlider from "react-bootstrap-range-slider";
import AnalyseTeam from "./AnalyseTeam";
import Clock from "./Clock";
import AnalyseGrenadesMapView from "./AnalyseGrenadesMapView";
import AnalyseProjectilesList from "./AnalyseProjectilesList";
import AnalyseRoundsList from "./AnalyseRoundsList";
import { databaseToBuyType } from "../utils/economy";
import { post_usage } from "../utils/usage";
import { SeededRandom } from "../utils/random";
import { getPlaylistOpponentTeamKey } from "../utils/playlist";
import { TeamsData } from "../pages/Matches";
import { fetchTeams } from "../utils/team_cache";
import AnalyseInspector from "./AnalyseInspector";
import { roundHasVersusType } from "../utils/analyse";
import { createPlaylist } from "../utils/playlist";
import { determineBombsite } from '../utils/analyse';
import { mapInfoAnalyse } from "../utils/maps";


export interface MatchRound {
  matchId: string;
  mapName: string;
  roundNum: number;
}

interface Player {
  name: string;
  team_key: string | undefined;
  steamId: number;
  nameFromOfficialMatch: boolean; // We prioritize names from official matches over user-uploaded replays
}

interface Players {
  [steamId: number]: Player;
}

const sideOptions = ["T", "CT"];

const visibilityOptions: {
  [key: string]: {
    main: string;
    extra: string;
    transform: string;
  }
} = {
  "Smoke Grenade": {
    "main": "m0.2266 0.036337v-0.13361c0-0.02242 0.00193-0.048802-0.002574-0.06863-0.003284-0.01449-0.010084-0.019735-0.015424-0.030818-0.003653-0.007493-0.00387-0.012895-0.00387-0.022701 0-0.003779-0.007869-0.011148-0.008994-0.01327-0.00409-0.007462-0.005591-0.021015-0.010927-0.026351-0.011896-0.011896-0.007308-0.037094-0.019264-0.048802-0.005808-0.005652-0.003251-0.022481-0.00896-0.028258-0.005652-0.005621-0.009897-0.017703-0.012272-0.023761-0.0064-0.016985-0.021926-0.032137-0.027641-0.048622 8.8041e-4 -0.008994-0.006275-0.003559-0.009025-0.008994-0.001592-0.003047 0.002336-0.011801 0-0.014175-0.001457-0.001422-0.011304-4.4021e-4 -0.012864-0.001287-0.0093355 0.003529-0.018983 0.021357-0.024384 0.032128 0.0020999-0.014175-0.061011-0.007714-0.069597-0.007714h-0.061759c-0.0074634 0-0.019484-0.00237-0.026666 0-0.014737 0.004873-0.025947 0.053923-0.020483 0.053923 0.002607 0 0.032098-4.402e-4 0.032098 0.001862v0.048022c0 0.012332-0.0077443 0.01942-0.0077443 0.032785 0 0.007805-0.025667 0.008242-0.033471 0.009617-0.015893 0.002777-0.07706 9.346e-5 -0.060293 0.033628 6.874e-4 0.036563 0 0.073282 0 0.10991v0.51282c0 0.012739 0.038062 0.024855 0.048396 0.028945 0.067162 0.02626 0.13048 0.020419 0.19886 0.006183 0.022667-0.004714 0.050364-0.012647 0.068692-0.026541 0.005591-0.004217 0.007744-8.804e-4 0.007744-0.007525v-0.6225c0.004537 0.020008 0.01056 0.034742 0.022107 0.052802 0.002912 0.004466 0.013612 0.015705 0.015363 0.020544 0.006621 0.017672 0 0.050988 0 0.070097v0.40197c0 0.00659-0.007744 0.008492-0.007744 0.01989 0 0.010554-0.001388 0.010274 0.009055 0.010274 0.006962 0 0.02811-0.004486 0.031293-0.006721 0.012177-0.008397 0.010304-0.021607 0.010304-0.039186 3.115e-5 -0.088706 3.115e-5 -0.17729 3.115e-5 -0.26596z",
    "extra": "m-0.13048-0.15286-0.001107 0.068209c-1.5407e-4 0.0094907-0.00312 0.01501 0.007153 0.016297 0.047959 0.0059323 0.064748 0.0099964 0.11261 0.015989 0.012864 0.0016243 0.027588-0.0027159 0.040326-0.0042735 0.028413-0.003588 0.021854-0.0026393 0.050268-0.0062291 0.020019-0.0025296 0.02001 7.075e-5 0.020371-0.020534l0.0011072-0.063084c-7e-7 -0.001271-5.06e-6 -0.002584-3.621e-5 -0.003803-0.025041 0.002686-0.030582 0.003078-0.055718 0.005686-0.021732 0.002342-0.032785 0.00738-0.054704 0.005179-0.050457-0.00512-0.069878-0.008285-0.12027-0.013436zm0 0.37228-0.001107 0.068209c-1.5407e-4 0.009491-0.00312 0.01501 0.007153 0.016297 0.047959 0.005932 0.064748 0.009996 0.11261 0.015989 0.012864 0.001626 0.027588-0.002716 0.040326-0.004273 0.028413-0.003592 0.021854-0.002639 0.050268-0.006229 0.020019-0.00253 0.02001 7.076e-5 0.020371-0.020534l0.0011072-0.063084c-4.2e-7 -0.00127-5.06e-6 -0.002603-3.621e-5 -0.003821-0.025041 0.002686-0.030582 0.003096-0.055718 0.005704-0.021732 0.002345-0.032785 0.00738-0.054704 0.005179-0.050457-0.005119-0.069878-0.008285-0.12027-0.013437z",
    "transform": "scale(24, 24) translate(0.5, 0.5)",
  },
  "Fire": {
    "main": "m-0.0019382 0.48368c0.19991 0 0.36194-0.12065 0.36194-0.33178 0-0.090485-0.030162-0.24129-0.15081-0.36194 0.015081 0.090485-0.075404 0.12065-0.075404 0.12065 0.045243-0.15081-0.075404-0.36194-0.25637-0.3921 0.021535 0.12065 0.030162 0.24129-0.12065 0.36194-0.075404 0.060323-0.12065 0.16462-0.12065 0.27146 0 0.21113 0.16203 0.33178 0.36194 0.33178z",
    "extra": "",
    "transform": "scale(21, 21) translate(0.6, 0.57)",
  },
  "Flashbang": {
    "main": "m-0.077609-0.34454v-0.0586c-0.02342-0.002934-0.035147-0.014151-0.035147-0.033719 0-0.012691 0.004385-0.020996 0.013155-0.024881l0.0293-0.004417h0.16115l0.13627 0.21826v0.46143l-0.068898 0.033719-0.021923-0.004386c-0.004881-0.008803-0.002973-0.017108 0.005813-0.024916 0.00781-0.006876 0.019568-0.014649 0.035213-0.023487l0.020464-0.010267v-0.41156l-0.058571-0.033685v0.068865l-0.035213 0.032224v0.068865h-0.014648v0.070293h0.014649v0.07325h-0.014649v0.070327h0.014649v0.07325h-0.014649v0.070293h0.014649v0.067404l0.035213 0.060062v0.04983c0 0.013653-0.006844 0.020464-0.020529 0.020464h-0.26078c-0.01372-6.642e-5 -0.020496-0.006876-0.020496-0.020529v-0.04983l0.033685-0.060062v-0.067404h0.016112v-0.070293h-0.016112v-0.07325h0.016112v-0.070293h-0.016112v-0.07325h0.016112v-0.07026h-0.016112v-0.068865l-0.033685-0.032224v-0.093747c0-0.013653 0.006776-0.020529 0.020463-0.020529h0.02053z",
    "extra": "",
    "transform": "scale(24, 24) translate(0.5, 0.5)",
  },
  "HE Grenade": {
    "main": "m0.17366-0.39531c0.003951 0.023207 0.020656 0.041088 0.03091 0.060919 0.013154 0.025434 0.023732 0.053167 0.035136 0.079525 0.009003 0.020881 0.018006 0.041538 0.030085 0.06067 0.011654 0.018381 0.01563 0.036987 0.014379 0.058468-0.00345 0.060394 0.003077 0.12104 0.003077 0.18143 0 0.028184 0.0026 0.057793-3.7456e-4 0.085851-9.2419e-4 0.008754-0.004701 0.01548-0.013505 0.017556-0.005477 0.001251-0.017631 0.002326-0.019456-0.005153-0.002374-0.009703 0.010204-0.010953 0.012204-0.018581 0.002651-0.010128 0-0.020956 0-0.03121 0-0.032835 1.5005e-4 -0.065646-7.5e-5 -0.098429-4.7616e-4 -0.057018-0.003251-0.11226-0.018081-0.16715-0.002224-0.008327-0.006277-0.021256-0.012528-0.027359-0.005251-0.005127-0.003876-0.01473-0.009403-0.019681-0.01603-0.014354-0.023257-0.034236-0.035211-0.053117-0.005477-0.008653-0.011628-0.01558-0.018156-0.023358-0.004427-0.005227-0.004252-0.01413-0.010078-0.017606-0.010628-0.006327-0.029309-0.0033-0.041563-0.003851-0.010503-5.0154e-4 -0.03166-0.003951-0.040488 0.003726-0.0046016 0.003977-0.0014253 0.022157-0.0014253 0.027909 0 0.009128-0.0045514 0.027909 0.0060268 0.03231 0.0079522 0.003352 0.017181 0.002876 0.025558 0.003525 0.013854 0.001076 0.027359 0.004577 0.040788 0.007902 0.024808 0.006176 0.039813 0.01548 0.057894 0.033486 0.020106 0.019981 0.023657 0.045815 0.032561 0.071548 0.010678 0.03086 0.01578 0.057718 0.011979 0.090576-0.005526 0.04759-0.028309 0.09728-0.058344 0.13432-0.037538 0.046289-0.1018 0.056618-0.15615 0.068872-0.027109 0.006101-0.056118 0.009953-0.083329 2e-3 -0.023458-0.006903-0.04704-0.01438-0.070347-0.021957-0.015605-0.005102-0.025583-0.016481-0.039388-0.025058-0.008252-0.005153-0.01373-0.012529-0.020656-0.019181-0.008578-0.0081525-0.01668-0.012179-0.022982-0.022932-0.027534-0.04694-0.040989-0.098182-0.039138-0.15285 0.001425-0.042064 0.012078-0.08538 0.038037-0.11854 0.013154-0.016931 0.023657-0.027934 0.043489-0.035537 0.021457-0.008228 0.042989-0.013029 0.064346-0.020382 0.012729-0.00435 0.037862 0.006101 0.041038-0.010979 0.0017017-0.009253 0.0047768-0.047215-0.0078281-0.047215-0.010578-5.002e-5 -0.0052264-0.12489-0.0052264-0.13777-0.013079 6.7613e-4 -0.028659 0.002374-0.041038-0.00305-0.005153-0.002224-0.008553-0.006402-0.007427-0.012229 7.2375e-4 -0.003951 0.002975-0.007627 0.005302-0.010853 0.0033-0.004651 0.002876-0.005277 0.005002-0.010054 0.0041509-0.009103 0.012179-0.011504 0.021357-0.011754 0.011479-2.7509e-4 0.015305-0.008327 0.026608-0.008327h0.099307c0.0041514 0 0.038137 0.001774 0.038737-9.0185e-4 0.0013751-0.006603-8.0001e-4 -0.013829-3.4917e-4 -0.020556 5.0155e-4 -0.007276 0.01443-0.00545 0.018956-0.005127 0.010204 7.2375e-4 0.014555 0.007552 0.014555 0.018531 0 4e-3 -0.005577 0.007252-0.001476 0.011029 0.003326 0.003077 0.007276 0.005352 0.010428 0.008653 0.006978 0.007228 0.007402 0.021332 0.013079 0.030235 0.013679 0.021232 0.015455 0.053367 0.033186 0.071698z",
    "extra": "",
    "transform": "scale(24, 24) translate(0.5, 0.66)",
  },
}

const zeroPad = (num: number, places: number) => String(num).padStart(places, '0')

const Analyse = ({ roundsData, projectileCache, playlist, csVersion, onSelectMatchesClick }: {
  roundsData: RoundData[] | undefined,
  projectileCache: CachedProjectiles,
  playlist: Playlist,
  csVersion: string,
  onSelectMatchesClick: () => void,
}
) => {
  const [teams, setTeams] = useState<TeamsData>(JSON.parse(sessionStorage.getItem('teamscache') || '{}'));

  const [currentSides, setCurrentSides] = useState<string[]>(playlist.side ? [playlist.side] : ["T"]);
  const [currentPlayers, setCurrentPlayers] = useState<string[]>([]);
  const [currentVisibility, setCurrentVisibility] = useState<string[]>([]);
  const [currentAnalysisType, setCurrentAnalysisType] = useState<string[]>(['replay']);
  const [currentEconomyType, setCurrentEconomyType] = useState<string[]>([]);
  const [currentHeroBuyTypes, setCurrentHeroBuyTypes] = useState<string[]>([]);
  const [currentOpponentBuyTypes, setCurrentOpponentBuyTypes] = useState<string[]>([]);
  const [currentHeroAWPType, setCurrentHeroAWPType] = useState<string[]>([]);
  const [currentOpponentAWPType, setCurrentOpponentAWPType] = useState<string[]>([]);
  const [selectOpponentTeam, setSelectOpponentTeam] = useState<boolean>(false);
  const [hoveredRound, setHoveredRound] = useState<MatchRound | undefined>(undefined);
  const [hoveredPlayerSteamId, setHoveredPlayerSteamId] = useState<number | undefined>(undefined);
  const [selectedRound, setSelectedRound] = useState<MatchRound | undefined>(undefined);
  const [selectedRoundType, setSelectedRoundType] = useState<"default" | "hover">("default");
  const [activePlayerSteamId, setActivePlayerSteamId] = useState<number | undefined>(undefined);
  const [currentTick, setCurrentTick] = useState<number>(0);
  const [currentTickRange, setCurrentTickRange] = useState<number[]>([0, 0]);
  const [autoPlay, setAutoPlay] = useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [upperView, setUpperView] = useState<boolean>(true);
  const [heatmapSmoothing, setHeatmapSmoothing] = useState<number>(1.0);
  const [marqueeRect, setMarqueeRect] = useState<number[] | undefined>(undefined);
  const [rectType, setRectType] = useState<'all' | 'landing' | undefined>(undefined);
  const [selectedRounds, setSelectedRounds] = useState<Set<string> | undefined>(undefined);
  const [invertSelectedRounds, setInvertSelectedRounds] = useState<boolean>(false);
  const [selectedProjectiles, setSelectedProjectiles] = useState<CachedProjectiles | undefined>(undefined);
  const [toBeSelectedRounds, setToBeSelectedRounds] = useState<Set<string> | undefined>(undefined);
  const [hoveredProjectile, setHoveredProjectile] = useState<number | undefined>(undefined);
  const [roundSelectedFromGrenadesView, setRoundSelectedFromGrenadesView] = useState<boolean>(false);
  const [currentVersusTypes, setCurrentVersusTypes] = useState<string[]>([]);
  const [currentBombsites, setCurrentBombsites] = useState<string[]>([]);

  useEffect(() => { post_usage('analyse_window') }, []);
  useEffect(() => { post_usage(`analyse_window_${currentAnalysisType}`) }, [currentAnalysisType]);

  useEffect(() => {
    if (!sessionStorage.getItem('teamscache')) {
      fetchTeams().then((data) => { setTeams(data); })
    }
  }, []);

  useEffect(() => {
    let animationFrameId: number | null = null;
    let lastTime: number | undefined = undefined;

    const update = (time: number) => {
      animationFrameId = requestAnimationFrame(update);
      if (autoPlay) {
        const tickRate = csVersion === "cs2" ? 64 : 128;
        const deltaTime = lastTime ? time - lastTime : 0;
        setCurrentTick(prevTick => prevTick + (tickRate / 1000) * playbackSpeed * deltaTime);
        lastTime = time;
      } else {
        lastTime = undefined;
      }
    };
    update(0);
    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [autoPlay, playbackSpeed]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const currentMapName = playlist.mapname;
      const hltvMapName = currentMapName?.replace('de_', '');

      if (event.key === " " || event.keyCode === 32) {
        setAutoPlay(prevAutoPlay => !prevAutoPlay);
      }
      else if (event.key === "m" || event.keyCode === 77) {
        togglePlaybackSpeed(true);
      }
      else if (event.key === "n" || event.keyCode === 78) {
        togglePlaybackSpeed(false);
      }
      else if ((event.key === "ArrowLeft" && !event.shiftKey) || (event.key === "," || event.keyCode === 188)) {
        setCurrentTick(prevTick => prevTick ? prevTick - 640 : prevTick);
      }
      else if ((event.key === "ArrowRight" && !event.shiftKey) || (event.key === "." || event.keyCode === 190)) {
        setCurrentTick(prevTick => prevTick ? prevTick + 640 : prevTick);
      }
      else if ((event.key === "l" || event.keyCode === 76) && hltvMapName) {
        toggleUpperView(hltvMapName);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [playlist.mapname, toggleUpperView]);

  // When playlist changes, reset selections
  useEffect(() => {
    resetSelectedRounds();
    resetSelectedProjectiles();
    setHoveredRound(undefined);
    setHoveredPlayerSteamId(undefined);
    setSelectedRound(undefined);
    setActivePlayerSteamId(undefined);
    setCurrentTick(0);
    setUpperView(true);
    setMarqueeRect(undefined);
    setRectType(undefined);
    setToBeSelectedRounds(undefined);
    setHoveredProjectile(undefined);
    setInvertSelectedRounds(false);
    setCurrentPlayers([]);
  }, [playlist]);

  function resetSelectedProjectiles() {
    updateSelectedProjectiles(undefined);
    setInvertSelectedRounds(false);
    setRectType(undefined);
    setMarqueeRect(undefined);
    updateSelectedRounds(undefined);
  }

  function updateSelectedProjectiles(newValue: CachedProjectiles | undefined) {
    setSelectedProjectiles(newValue);
    setSelectedRounds(getProjectileRounds(newValue));
  }

  function updateSelectedRounds(newValue: Set<string> | undefined) {
    setSelectedRounds(newValue);
    setSelectedProjectiles(undefined);
  }

  function resetSelectedRounds() {
    updateSelectedRounds(undefined);
    setRectType(undefined);
    setMarqueeRect(undefined);
    updateSelectedProjectiles(undefined);
    setInvertSelectedRounds(false);
  }

  function OnRectSelectRounds(
    rect: number[] | undefined,
    rectType: "all" | "landing" | undefined,
    dragStart: boolean,
    dragEnd: boolean,
    playerFrames: PlayerFrameData[],
  ) {
    setAutoPlay(false);
    setSelectedProjectiles(undefined);
    setMarqueeRect(rect);
    setRectType(rectType);

    if (dragStart) {
      // noop
    } else if (rect) {
      const newRounds = getRoundsInsideRect(playerFrames, rect, playlist.mapname || '', rectType);
      if (dragEnd && newRounds.size === 0) {
        resetSelectedRounds();
      } else if (dragEnd) {
        updateSelectedRounds(newRounds);
        setToBeSelectedRounds(undefined);
        setMarqueeRect(undefined);
        setRectType(undefined);
        setInvertSelectedRounds(false);
      } else {
        setToBeSelectedRounds(newRounds);
      }
    } else {
      resetSelectedProjectiles();
    }
  }

  function OnRectSelectGrenades(
    rect: number[] | undefined,
    rectType: "all" | "landing" | undefined,
    dragStart: boolean,
    dragEnd: boolean,
    players: Players,
    rounds: string[],
  ) {
    setAutoPlay(false);
    setMarqueeRect(rect);
    setRectType(rectType);

    if (dragStart) {
      // noop
    } else if (rect) {
      const newProjectiles = getProjectilesInsideRect(getFilteredProjectileCache(players, rounds), rect, rectType);
      if (dragEnd && Object.keys(newProjectiles).length === 0) {
        resetSelectedProjectiles();
      } else if (dragEnd) {
        setMarqueeRect(undefined);
        updateSelectedProjectiles(newProjectiles);
      } else {
        updateSelectedProjectiles(newProjectiles);
      }
    } else {
      resetSelectedProjectiles();
    }
  }

  const handleRemoveProjectile = (key: number) => {
    const updatedProjectiles = { ...selectedProjectiles };
    delete updatedProjectiles[key];
    updateSelectedProjectiles(updatedProjectiles);
  };

  const handleHoverProjectile = (key: number | undefined) => {
    setHoveredProjectile(key);
  }

  function togglePlaybackSpeed(faster: boolean) {
    const playbackSpeedOptions = [0.5, 1, 1.5, 2, 4, 8];
    const currentIndex = playbackSpeedOptions.indexOf(playbackSpeed);
    let newIndex = faster ? currentIndex + 1 : currentIndex - 1;
    if (newIndex < 0 || newIndex >= playbackSpeedOptions.length) {
      newIndex = 0;
    }
    setPlaybackSpeed(playbackSpeedOptions[newIndex]);
  }

  function getFilteredProjectileCache(players?: Players, rounds?: string[]): CachedProjectiles {
    if (roundsData === undefined || roundsData.length === 0) {
      return {};
    }
    return Object.entries(projectileCache).reduce((accumulator: CachedProjectiles, [key, projectile]) => {
      if (currentVisibility.length === 0 || currentVisibility.includes(projectile.projectileType) || (currentVisibility.includes("Fire") && (projectile.projectileType === "Molotov" || projectile.projectileType === "Incendiary Grenade")) || selectedProjectiles !== undefined && projectile.uniqueID in selectedProjectiles) {
        if ((projectile.thrower && playerIsShown(projectile.thrower.steamID, players) && currentSides.includes(projectile.thrower.side)) || activeRound !== undefined || selectedProjectiles !== undefined && projectile.uniqueID in selectedProjectiles) {
          if (isProjectileInRange(projectile, currentTickRange[0], currentTickRange[1]) || selectedProjectiles !== undefined && projectile.uniqueID in selectedProjectiles) {
            const roundId = `${projectile.matchId}.${projectile.mapName}.${projectile.roundNum}`;
            if (rounds === undefined || rounds.includes(roundId)) {
              accumulator[Number(key)] = projectile;
            }
          }
        }
      }
      return accumulator;
    }, {});
  }

  function getProjectilesInsideRect(projectiles: CachedProjectiles, rect: number[], rectType: "all" | "landing" | undefined): CachedProjectiles {
    if (roundsData === undefined || roundsData.length === 0) {
      return {};
    }
    return Object.entries(projectiles).reduce((accumulator: CachedProjectiles, [key, p]) => {
      const projectile: ProjectileInfo = p;
      const checkedPositions = rectType === "all" ? [projectile.throwerPosition, projectile.endPosition] : [projectile.endPosition];
      checkedPositions.forEach((pos) => {
        const screenPos = world_to_screen(pos, roundsData[0].mapName || '');
        if (screenPos) {
          const [x, y] = screenPos;
          if (x >= rect[0] && x <= rect[2] && y >= rect[1] && y <= rect[3]) {
            accumulator[Number(key)] = projectile;
          }
        }
      });
      return accumulator;
    }, {});
  }

  function getRoundsInsideRect(playerFrames: PlayerFrameData[], rect: number[], mapName: string, rectType: "all" | "landing" | undefined): Set<string> {
    const fullMapName = 'de_' + mapName;
    const mapInfo = mapInfoAnalyse[fullMapName as keyof typeof mapInfoAnalyse];
    
    return playerFrames.reduce((accumulator: Set<string>, p) => {
      // Filter by layer only for nuke and vertigo maps
      if ((mapName === 'nuke' || mapName === 'vertigo') && mapInfo && mapInfo.splitZ !== undefined) {
        const playerOnUpperLayer = p.z >= mapInfo.splitZ;
        if (upperView !== playerOnUpperLayer) {
          return accumulator; // Skip this player as they're on the wrong layer
        }
      }
      
      const screenPos = world_to_screen(
        [p.x, p.y, p.z],
        fullMapName,
      );
      if (screenPos) {
        const [x, y] = screenPos;
        if (x >= rect[0] && x <= rect[2] && y >= rect[1] && y <= rect[3]) {
          const roundId = `${p.match_id}.${mapName}.${p.roundNum}`;
          accumulator.add(roundId);
        }
      }
      return accumulator;
    }, new Set<string>());
  }


  function getProjectileRounds(projectiles: CachedProjectiles | undefined): Set<string> {
    if (projectiles === undefined) {
      return new Set<string>();
    }
    return Object.entries(projectiles).reduce((accumulator: Set<string>, [key, projectile]) => {
      if (projectile.matchId !== undefined && projectile.roundNum !== undefined) {
        accumulator.add(`${projectile.matchId}.${projectile.mapName}.${projectile.roundNum}`);
      }
      return accumulator;
    }, new Set<string>());
  }

  function playerIsShown(steamId: number, players: Players | undefined): boolean {
    return currentPlayers.includes(steamId.toString()) || currentPlayers.length === 0 && (players === undefined || players[steamId] !== undefined);
  }

  function sideIsShown(roundData: RoundData, side: string): boolean {
    if (!selectOpponentTeam) {
      return (team_names_equal(playlist.team_key, roundData.tTeamKey) && side === "T" && currentSides.includes("T")) ||
        (team_names_equal(playlist.team_key, roundData.ctTeamKey) && side === "CT" && currentSides.includes("CT"));
    } else {
      return (team_names_equal(playlist.team_key, roundData.ctTeamKey) && side === "T" && currentSides.includes("T")) ||
        (team_names_equal(playlist.team_key, roundData.tTeamKey) && side === "CT" && currentSides.includes("CT"));
    }
  }

  function sideHasAWP(roundData: RoundData, side: string): boolean {
    // Get first frame data for the specified side
    const teamFrameData = side === "T" ? roundData.frames[0].t : roundData.frames[0].ct;

    // Check if any player has an AWP in their inventory
    return teamFrameData.players.some((p) =>
      p.inventory.some((i) =>
        i.weaponName?.toLowerCase() === "awp"
      )
    );
  }

  let projectiles: Projectile[] = [];
  let filteredRoundCount: number = 0;
  let filteredRounds: string[] = [];
  let explosions: GrenadeData[] = [];
  let smokes: SmokeInfo[] = [];
  let fires: FireInfo[] = [];
  let playerFrames: PlayerFrameData[] = [];
  let players: Players = {};
  let allPlayers: Players = {};
  let weaponFires: WeaponFireData[] = [];
  let bullets: DamageData[] = [];
  let damages: DamageData[] = [];
  let kills = new Map<string, KillData>();
  let deaths = new Map<string, KillData>();
  let bomb = null;
  let bombPlanted = false;
  let bombTimeLeft = 0;
  let bombDefusing = false;
  let activeRound = selectedRound;
  let activeRoundData = undefined;
  let activeRoundMetadata = undefined;
  let maxTick = 10000;

  if (roundsData !== undefined && roundsData.length > 0) {
    activeRoundData = activeRound !== undefined ? roundsData.find((r) => activeRound && r.matchId === activeRound.matchId && r.roundNum === activeRound.roundNum) : undefined;
    activeRoundMetadata = activeRound !== undefined ? playlist.rounds[activeRound.matchId].find((r) => r.roundnum === activeRound?.roundNum) : undefined;

    const fireToProjectileMap = new Map(Object.entries(projectileCache).map(([k, v]) => [v.fireUniqueID, Number(k)]));

    // Find out max tick
    maxTick = Math.min(roundsData.reduce((acc, cur) => Math.max(acc, cur.frames[cur.frames.length - 1].tick), 0), 18000);
    if (currentTickRange[1] === 0) {
      setCurrentTickRange([0, maxTick / 2]);
    }

    for (let i = 0; i < roundsData.length; i++) {
      const roundData: RoundData = roundsData[i];

      if (currentEconomyType.length > 0) {
        if (currentEconomyType[0] === "full" && (roundData.tBuyType !== "Full Buy" || roundData.ctBuyType !== "Full Buy")) {
          continue;
        } else if (currentEconomyType[0] === "pistol" && (roundData.tBuyType !== "Full Eco" || roundData.ctBuyType !== "Full Eco")) {
          continue;
        } else if (currentEconomyType[0] === "custom") {
          if (currentHeroBuyTypes.length > 0) {
            if (roundData.tTeamKey === playlist.team_key && !currentHeroBuyTypes.includes(databaseToBuyType(roundData.tBuyType)) || roundData.ctTeamKey === playlist.team_key && !currentHeroBuyTypes.includes(databaseToBuyType(roundData.ctBuyType))) {
              continue;
            }
          }
          if (currentOpponentBuyTypes.length > 0) {
            if (roundData.tTeamKey !== playlist.team_key && !currentOpponentBuyTypes.includes(databaseToBuyType(roundData.tBuyType)) || roundData.ctTeamKey !== playlist.team_key && !currentOpponentBuyTypes.includes(databaseToBuyType(roundData.ctBuyType))) {
              continue;
            }
          }
        }
      }

      if (currentHeroAWPType.length > 0) {
        const heroSide = roundData.tTeamKey === playlist.team_key ? "T" : "CT";
        if ((!sideHasAWP(roundData, heroSide) && currentHeroAWPType.includes("awp")) || (sideHasAWP(roundData, heroSide) && currentHeroAWPType.includes("no-awp"))) {
          continue;
        }
      }
      if (currentOpponentAWPType.length > 0) {
        const opponentSide = roundData.tTeamKey === playlist.team_key ? "CT" : "T";
        if ((!sideHasAWP(roundData, opponentSide) && currentOpponentAWPType.includes("awp")) || (sideHasAWP(roundData, opponentSide) && currentOpponentAWPType.includes("no-awp"))) {
          continue;
        }
      }

      if (currentVersusTypes.length > 0) {
        let matchesVersusType = false;
        for (const versusType of currentVersusTypes) {
          const side = sideIsShown(roundData, "T") ? "T" : "CT";
          if (roundHasVersusType(roundData, side, versusType)) {
            matchesVersusType = true;
            break;
          }
        }
        if (!matchesVersusType) {
          continue;
        }
      }

      if (currentBombsites.length > 0) {
        const bombsite = determineBombsite(roundData, roundData.mapName || '');
        if (!bombsite || !currentBombsites.includes(bombsite)) {
          continue;
        }
      }

      if (sideIsShown(roundData, "T") || sideIsShown(roundData, "CT")) {
        filteredRoundCount++;
        filteredRounds.push(`${roundData.matchId}.${roundData.mapName?.replace('de_', '')}.${roundData.roundNum}`);
      }

      const playlistTeamPlayers = selectOpponentTeam ?
        // Get opponent team players when selectOpponentTeam is true
        (team_names_equal(playlist.team_key, roundData.tTeamKey) ? roundData.frames[0].ct.players : team_names_equal(playlist.team_key, roundData.ctTeamKey) ? roundData.frames[0].t.players : []) :
        // Get playlist team players when selectOpponentTeam is false (original behavior)
        (team_names_equal(playlist.team_key, roundData.tTeamKey) ? roundData.frames[0].t.players : team_names_equal(playlist.team_key, roundData.ctTeamKey) ? roundData.frames[0].ct.players : []);
      const playlistTeamPlayersShort = playlistTeamPlayers.map((p) => {
        return {
          ...p,
          name: getShortPlayerName(p.name, playlistTeamPlayers.map((p) => p.name)),
        };
      });

      for (let i = 0; i < playlistTeamPlayersShort.length; i++) {
        const p = playlistTeamPlayersShort[i];
        if (!players[p.steamID] || (!players[p.steamID].nameFromOfficialMatch && !roundData.matchId?.includes('user-upload'))) {
          players[p.steamID] = {
            name: p.name,
            steamId: p.steamID,
            team_key: selectOpponentTeam ? (team_names_equal(playlist.team_key, roundData.ctTeamKey) ? roundData.tTeamKey : roundData.ctTeamKey) : playlist.team_key,
            nameFromOfficialMatch: !roundData.matchId?.includes('user-upload'),
          };
        }
      }

      const allTeamPlayers = roundData.frames[0].t.players.concat(roundData.frames[0].ct.players);
      const allTeamPlayersShort = allTeamPlayers.map((p) => {
        return {
          ...p,
          name: getShortPlayerName(p.name, allTeamPlayers.map((p) => p.name)),
        };
      });

      for (let i = 0; i < allTeamPlayersShort.length; i++) {
        const p = allTeamPlayersShort[i];
        if (!allPlayers[p.steamID] || (!allPlayers[p.steamID].nameFromOfficialMatch && !roundData.matchId?.includes('user-upload'))) {
          allPlayers[p.steamID] = {
            name: p.name,
            steamId: p.steamID,
            team_key: selectOpponentTeam ? (team_names_equal(playlist.team_key, roundData.ctTeamKey) ? roundData.tTeamKey : roundData.ctTeamKey) : playlist.team_key,
            nameFromOfficialMatch: !roundData.matchId?.includes('user-upload'),
          };
        }
      }

      if (selectedRounds !== undefined && selectedRounds.size > 0 &&
        (selectedRounds && !selectedRounds.has(`${roundData.matchId}.${roundData.mapName?.replace('de_', '')}.${roundData.roundNum}`) && !invertSelectedRounds) ||
        (selectedRounds && selectedRounds.has(`${roundData.matchId}.${roundData.mapName?.replace('de_', '')}.${roundData.roundNum}`) && invertSelectedRounds)
      ) {
        continue;
      }

      if (activeRound && (activeRound.matchId !== roundData.matchId || activeRound.roundNum !== roundData.roundNum)) {
        continue;
      }

      if (currentAnalysisType[0] === 'replay' || currentAnalysisType[0] === 'grenades') {
        const tickStart: number = Math.max(...roundData.frames.map((f) => f.tick).filter((t) => t <= currentTick && t >= 0 && t < maxTick));
        const tickEnd: number = Math.min(...roundData.frames.map((f) => f.tick).filter((t) => t >= currentTick && t > 0 && t <= maxTick && t !== tickStart));
        const frameStart: FrameData = roundData.frames.find((f) => f.tick === tickStart)!;
        const frameEnd: FrameData = roundData.frames.find((f) => f.tick === tickEnd)!;
        const currentFrameIndex = roundData.frames.indexOf(frameStart);

        for (let i = 0; i < roundData.kills.length; i++) {
          let killData = roundData.kills[i];
          let compositeKey = `${killData.tick}-${killData.attackerSteamID}-${killData.victimSteamID}`;

          killData.matchId = roundData.matchId;
          killData.roundNum = roundData.roundNum;
          killData.attackerShortName = killData.attackerSteamID in allPlayers ? allPlayers[killData.attackerSteamID].name : killData.attackerName;

          killData.victimShortName = killData.victimSteamID in allPlayers ? allPlayers[killData.victimSteamID].name : killData.victimName;

          if (killData.tick < currentTick && currentSides.includes(killData.attackerSide) && playerIsShown(killData.attackerSteamID, players)) {
            kills.set(compositeKey, killData);
          } else if (killData.tick < currentTick && currentSides.includes(killData.victimSide) && playerIsShown(killData.victimSteamID, players)) {
            deaths.set(compositeKey, killData);
          }
        }

        if (!team_names_equal(playlist.team_key, roundData.tTeamKey) && !team_names_equal(playlist.team_key, roundData.ctTeamKey)) {
          continue;
        }

        if (frameStart && frameEnd) {
          const t = (currentTick - tickStart) / (frameEnd.tick - frameStart.tick);
          if (sideIsShown(roundData, "T") || activeRound !== undefined) {
            for (let i = 0; i < frameStart.t.players.length; i++) {
              if ((playerIsShown(frameStart.t.players[i].steamID, players) || activeRound !== undefined) && frameStart.t.players[i].isAlive) {
                const pStart = frameStart.t.players[i];
                const pEnd = frameEnd.t.players.find((p) => p.steamID === pStart.steamID)!;
                if (pStart && pEnd) {
                  const pFrame = interpolatePlayerFramesLinear(pStart, pEnd, t)
                  pFrame.match_id = roundData.matchId;
                  pFrame.roundNum = roundData.roundNum;
                  playerFrames.push(pFrame);
                }
              }
            }
          }
          if (sideIsShown(roundData, "CT") || activeRound !== undefined) {
            for (let i = 0; i < frameStart.ct.players.length; i++) {
              if ((playerIsShown(frameStart.ct.players[i].steamID, players) || activeRound !== undefined) && frameStart.ct.players[i].isAlive) {
                const pStart = frameStart.ct.players[i];
                const pEnd = frameEnd.ct.players.find((p) => p.steamID === pStart.steamID)!;
                if (pStart && pEnd) {
                  const pFrame = interpolatePlayerFramesLinear(pStart, pEnd, t)
                  pFrame.match_id = roundData.matchId;
                  pFrame.roundNum = roundData.roundNum;
                  playerFrames.push(pFrame);
                }
              }
            }
          }

          const zSortedProjectiles = frameStart.projectiles.sort((a, b) => a.z - b.z);
          for (let i = 0; i < zSortedProjectiles.length; i++) {
            const pStart = zSortedProjectiles[i];
            const pEnd = frameEnd.projectiles.find((p) => p.uniqueID === pStart.uniqueID) ?? undefined;

            if (!pStart) {
              continue;
            }

            if (pStart.projectileType === "Decoy Grenade") {
              continue;
            }

            if (currentVisibility.length === 0 || currentVisibility.includes(pStart.projectileType) || currentVisibility.includes("Fire") && (pStart.projectileType === "Molotov" || pStart.projectileType === "Incendiary Grenade")) {
              if (pStart && pEnd && pStart.projectileType === "Smoke Grenade" && pStart.z === pEnd.z && pStart.uniqueID in projectileCache) {
                const projectile = projectileCache[pStart.uniqueID];
                if (projectile && projectile.thrower && (playerIsShown(projectile.thrower.steamID, players) && currentSides.includes(projectile.thrower.side) || activeRound !== undefined)) {
                  smokes.push({
                    uniqueID: pStart.uniqueID,
                    position: [pStart.x, pStart.y, pStart.z],
                    startTick: projectile.endTick,
                    endTick: projectile.turnOffTick,
                    side: projectile.side,
                    thrower: projectile.thrower,
                  });
                }
              } else if (pStart && pEnd && pStart.uniqueID in projectileCache && (pStart.projectileType !== "HE Grenade" || roundData.grenades.some((g) => g.entityId === pStart.uniqueID && getExplosionTick(g, csVersion) >= currentTick))) {
                const projectile = projectileCache[pStart.uniqueID];
                if (projectile && projectile.thrower && (playerIsShown(projectile.thrower.steamID, players) && currentSides.includes(projectile.thrower.side) || activeRound !== undefined)) {
                  projectiles.push({
                    data: interpolateProjectileFramesLinear(pStart, pEnd, t),
                    throwerPosition: projectile.throwerPosition,
                    positions: projectile.positions,
                    visualPositions: projectile.visualPositions,
                    visualPositionsFlat: projectile.visualPositionsFlat,
                    minZ: projectile.minZ,
                    maxZ: projectile.maxZ,
                    endZ: projectile.endZ,
                    startTick: projectile.startTick,
                    endTick: projectile.endTick,
                    currentFrameIndex: currentFrameIndex,
                    angularVelocity: projectile.angularVelocity,
                    startAngle: projectile.startAngle,
                    side: projectile.side,
                  });
                }
              }
            }
          }

          if (currentVisibility.length === 0 || currentVisibility.includes("Fire")) {
            const frameEndFiresMap = new Map(frameEnd.fires.map((fire) => [fire.uniqueID, fire]));
            const firesMap = new Map(fires.map((fire) => [fire.uniqueID, fire]));

            for (let i = 0; i < frameStart.fires.length; i++) {
              const pStart = frameStart.fires[i];
              const pEnd = frameEndFiresMap.get(pStart.uniqueID);
              if (pStart && pEnd && !firesMap.has(pStart.uniqueID)) {
                const projectileUniqueID = fireToProjectileMap.get(pStart.uniqueID);
                if (projectileUniqueID !== undefined) {
                  const projectile = projectileCache[projectileUniqueID];
                  if (projectile && projectile.thrower && (playerIsShown(projectile.thrower.steamID, players) && currentSides.includes(projectile.thrower.side) || activeRound !== undefined)) {
                    fires.push({
                      uniqueID: pStart.uniqueID,
                      position: [pStart.x, pStart.y, pStart.z],
                      startTick: projectile.turnOnTick,
                      endTick: projectile.turnOffTick,
                      side: projectile.side,
                    });
                  }
                }
              }
            }
          }
          for (let i = 0; i < roundData.grenades.length; i++) {
            if (currentVisibility.length === 0 || currentVisibility.includes(roundData.grenades[i].grenadeType)) {
              if (roundData.grenades[i].grenadeType === "Flashbang" || roundData.grenades[i].grenadeType === "HE Grenade") {
                const projectile = projectileCache[roundData.grenades[i].entityId];
                if (projectile && projectile.thrower && (playerIsShown(projectile.thrower.steamID, players) && currentSides.includes(projectile.thrower.side) || activeRound !== undefined)) {
                  const explosionTick = getExplosionTick(roundData.grenades[i], csVersion);
                  if (explosionTick < currentTick && explosionTick + 2000 > currentTick) {
                    explosions.push(roundData.grenades[i]);
                  }
                }
              }
            }
          }

          if (activeRound !== undefined) {
            for (let i = 0; i < roundData.weaponFires.length; i++) {
              if (roundData.weaponFires[i].tick < currentTick && roundData.weaponFires[i].tick + 200 > currentTick && roundData.weaponFires[i].weaponClass !== "Equipment" && roundData.weaponFires[i].weaponClass !== "Grenade") {
                weaponFires.push(roundData.weaponFires[i]);
              }
            }
            for (let i = 0; i < roundData.damages.length; i++) {
              if (roundData.damages[i].tick - roundData.damages[i].distance / 50 < currentTick && roundData.damages[i].tick > currentTick && roundData.damages[i].weaponClass !== "Equipment" && roundData.damages[i].weaponClass !== "Grenade") {
                bullets.push(roundData.damages[i]);
              }
              if (roundData.damages[i].tick < currentTick && roundData.damages[i].tick + 500 > currentTick) {
                damages.push(roundData.damages[i]);
              }
            }
            if (frameStart.t.players.every((p) => !p.hasBomb)) {
              bombPlanted = frameStart.bombPlanted;
              bombTimeLeft = 40 * 128 - (currentTick - roundData.bombPlantTick);
              bomb = interpolateBombFramesLinear(frameStart.bomb, frameEnd.bomb, t);
              bombDefusing = roundData.frames.some((f) => f.tick < currentTick && f.ct.players.some((p) => p.isDefusing && roundData?.roundEndReason === "BombDefused"));
            }
          }

          playerFrames.sort((a, b) => Number(a.isAlive) - Number(b.isAlive));
        }
      }

      if (currentAnalysisType[0] === 'heatmap') {
        // Usage example with types for ticks and range
        const samples: number = 50;
        const startTick: number = currentTickRange[0];
        const endTick: number = currentTickRange[1];
        const interval: number = (endTick - startTick) / (samples - 1);
        const maxNoiseAmplitude: number = interval * 0.5;

        // Initialize the seeded random generator
        const randomGenerator = new SeededRandom(roundData.roundNum);

        // Create an array of ticks with deterministic noise
        const ticks: number[] = Array.from({ length: samples }, (_, i): number => {
          const baseTick: number = startTick + i * interval;
          const noise: number = (randomGenerator.next() - 0.5) * 2 * maxNoiseAmplitude;

          return baseTick + noise;
        });

        for (const tick of ticks) {
          const tickStart: number = Math.max(...roundData.frames.map((f) => f.tick).filter((t) => t <= tick && t >= 0 && t < maxTick));
          const tickEnd: number = Math.min(...roundData.frames.map((f) => f.tick).filter((t) => t >= tick && t > 0 && t <= maxTick && t !== tickStart));
          const frameStart: FrameData = roundData.frames.find((f) => f.tick === tickStart)!;
          const frameEnd: FrameData = roundData.frames.find((f) => f.tick === tickEnd)!;

          if (frameEnd === undefined || frameStart === undefined) {
            continue;
          }

          const t = (tick - tickStart) / (frameEnd.tick - frameStart.tick);

          if (sideIsShown(roundData, "T")) {
            for (let i = 0; i < frameStart.t.players.length; i++) {
              if (playerIsShown(frameStart.t.players[i].steamID, players) && frameStart.t.players[i].isAlive) {
                const pStart = frameStart.t.players[i];
                const pEnd = frameEnd.t.players.find((p) => p.steamID === pStart.steamID)!;
                if (pStart && pEnd) {
                  const pFrame = interpolatePlayerFramesLinear(pStart, pEnd, t)
                  pFrame.match_id = roundData.matchId;
                  pFrame.roundNum = roundData.roundNum;
                  playerFrames.push(pFrame);
                }
              }
            }
          }
          if (sideIsShown(roundData, "CT")) {
            for (let i = 0; i < frameStart.ct.players.length; i++) {
              if (playerIsShown(frameStart.ct.players[i].steamID, players) && frameStart.ct.players[i].isAlive) {
                const pStart = frameStart.ct.players[i];
                const pEnd = frameEnd.ct.players.find((p) => p.steamID === pStart.steamID)!;
                if (pStart && pEnd) {
                  const pFrame = interpolatePlayerFramesLinear(pStart, pEnd, t)
                  pFrame.match_id = roundData.matchId;
                  pFrame.roundNum = roundData.roundNum;
                  playerFrames.push(pFrame);
                }
              }
            }
          }
        }
      }
    }
  }

  function toggleUpperView(selectedMap: string) {
    if ((selectedMap === 'vertigo' || selectedMap === 'nuke')) {
      setUpperView(prevUpperView => !prevUpperView);
    }
  }

  const logMin = Math.log(100);
  const logMax = Math.log(20000);

  const scaleValueToLog = (value: number) => {
    return Math.exp(logMin + (value / 100) * (logMax - logMin));
  };

  const scaleValueFromLog = (value: number) => {
    return ((Math.log(value) - logMin) / (logMax - logMin)) * 100;
  };

  const playlistLength = Math.min(200, Object.values(playlist.rounds).reduce((total, currentRound) => total + currentRound.length, 0));
  const progress = playlistLength > 0 && roundsData !== undefined ? roundsData.length / playlistLength : 0;

  function onPlayerBeginHover(matchId: string, mapName: string, roundNum: number, steamId: number): void {
    // Filter by layer only for nuke and vertigo maps
    if ((mapName === 'de_nuke' || mapName === 'de_vertigo') && roundsData) {
      const roundData = roundsData.find(r => r.matchId === matchId && r.roundNum === roundNum);
      if (roundData) {
        const mapInfo = mapInfoAnalyse[mapName as keyof typeof mapInfoAnalyse];
        
        if (mapInfo && mapInfo.splitZ !== undefined) {
          // Find the player in the current tick's frame data
          const tickStart = Math.max(...roundData.frames.map((f) => f.tick).filter((t) => t <= currentTick && t >= 0));
          const frameStart = roundData.frames.find((f) => f.tick === tickStart);
          
          if (frameStart) {
            // Look for the player in both T and CT sides
            const allPlayers = [...frameStart.t.players, ...frameStart.ct.players];
            const player = allPlayers.find(p => p.steamID === steamId);
            
            if (player) {
              const playerOnUpperLayer = player.z >= mapInfo.splitZ;
              if (upperView !== playerOnUpperLayer) {
                return; // Don't hover if player is on wrong layer
              }
            }
          }
        }
      }
    }
    
    setHoveredRound({ matchId, mapName, roundNum });
    setHoveredPlayerSteamId(steamId);
  }

  function onPlayerEndHover(matchId: string, mapName: string, roundNum: number, steamId: number): void {
    setHoveredRound(undefined);
    setHoveredPlayerSteamId(undefined);
  }

  function resetActiveRound(): void {
    setSelectedRound(undefined);
    setHoveredRound(undefined);
    setActivePlayerSteamId(undefined);
    setHoveredPlayerSteamId(undefined);
    setActivePlayerSteamId(undefined);
  }

  function onPlayerClick(matchId: string, mapName: string, roundNum: number, steamId: number): void {
    if (selectedRound !== undefined && selectedRound.matchId === matchId && selectedRound.roundNum === roundNum) {
      resetActiveRound();
    } else {
      setActivePlayerSteamId(steamId);
      setSelectedRoundType("default");
      setSelectedRound({ matchId, mapName, roundNum });
    }
  }

  function handleProjectileClick(key: number, roundId: string, throwerSteamId: number): void {
    const parts = roundId.split('.');
    const matchId = parts.slice(0, 4).join('.'); // Join the first four parts with dots
    const mapName = parts[parts.length - 2]; // Second last element for mapName
    const roundNum = parseInt(parts[parts.length - 1]); // Last element for roundNum
    setCurrentTick(projectileCache[key].startTick);
    setActivePlayerSteamId(throwerSteamId);
    setSelectedRound({ matchId, mapName, roundNum });
    setHoveredProjectile(undefined);
    setCurrentAnalysisType(['replay']);
    setRoundSelectedFromGrenadesView(true);
  }

  function handleRoundClick(roundId: string): void {
    const parts = roundId.split('.');
    const matchId = parts.slice(0, 4).join('.'); // Join the first four parts with dots
    const mapName = parts[parts.length - 2]; // Second last element for mapName
    const roundNum = parseInt(parts[parts.length - 1]); // Last element for roundNum
    setRoundSelectedFromGrenadesView(false);
    if (selectedRound !== undefined && selectedRound.matchId === matchId && selectedRound.roundNum === roundNum && selectedRoundType === "default") {
      resetActiveRound();
    } else {
      setSelectedRound({ matchId, mapName, roundNum });
    }
    setSelectedRoundType("default");
  }

  function handleSelectedRoundBackClick(): void {
    setSelectedRound(undefined);
    setHoveredRound(undefined);
    setSelectedRoundType("default");
    if (roundSelectedFromGrenadesView) {
      setCurrentAnalysisType(['grenades']);
    }
    setRoundSelectedFromGrenadesView(false);
    setActivePlayerSteamId(undefined);
  }

  function handleRoundHover(roundId: string | undefined): void {
    if (roundId === undefined) {
      setHoveredRound(undefined);
      setSelectedRound(undefined);
      setSelectedRoundType("default");
      return;
    }

    const parts = roundId.split('.');

    // Assuming format: <date>.<teamA>.<teamB>.<eventname>.<mapname>.<roundnum>
    const matchId = parts.slice(0, 4).join('.'); // Join the first four parts with dots
    const mapNameIndex = parts.length - 2; // Second last element for mapName
    const roundNumIndex = parts.length - 1; // Last element for roundNum

    setHoveredRound({
      matchId: matchId,
      mapName: parts[mapNameIndex],
      roundNum: parseInt(parts[roundNumIndex]),
    });
    setSelectedRoundType("hover")
    setSelectedRound({
      matchId: matchId,
      mapName: parts[mapNameIndex],
      roundNum: parseInt(parts[roundNumIndex]),
    });
  }

  function handleRoundRemove(roundId: string): void {
    setSelectedRounds(prevSelectedRounds => {
      const newSelectedRounds = new Set(prevSelectedRounds);
      newSelectedRounds.delete(roundId);
      return newSelectedRounds;
    });
  }

  // Create a map of player steamIDs and their colors. Color is the index of playerColors array
  // Sort players based on their name to ensure consistent colors
  const playerColorsArray = ['#FF5733', '#FFD700', '#3357FF', '#FF33A6', '#30E0A0'];

  const playerColors = new Map<number, string>();
  for (const player of Object.values(players).sort((a, b) => a.name.localeCompare(b.name))) {
    playerColors.set(player.steamId, playerColorsArray[playerColors.size % playerColorsArray.length]);
  }

  const selectedRoundsKeys = Object.values(filteredRounds).flat().filter(round => {
    return toBeSelectedRounds !== undefined && toBeSelectedRounds.size > 0 ? toBeSelectedRounds.has(round) : (selectedRounds?.has(round) && !invertSelectedRounds) || (!selectedRounds?.has(round) && invertSelectedRounds);
  });

  const rounds = playlist.rounds;
  // Get flat array of rounds based on selectedRoundKeys
  const selectedRoundsList = Object.values(rounds).flat().filter(round => {
    return selectedRoundsKeys.includes(`${round.match_id}.${round.mapname}.${round.roundnum}`);
  });

  // Add this function to the Analyse component
  const handleReplayAllFilteredRounds = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (filteredRounds.length > 0) {
      // Create a playlist from the filtered rounds
      const filteredRoundsList = Object.values(rounds).flat().filter(round => {
        return filteredRounds.includes(`${round.match_id}.${round.mapname}.${round.roundnum}`);
      });
      
      const newPlaylist = createPlaylist(filteredRoundsList);

      sessionStorage.setItem('playlist', JSON.stringify(newPlaylist));
      const firstMatchId = Object.keys(newPlaylist.rounds)[0];
      const firstRoundNum = newPlaylist.rounds[firstMatchId][0].roundnum;
      if (firstMatchId && firstRoundNum !== undefined) {
        e.preventDefault();
        window.open(`/match/${firstMatchId}/${playlist.mapname}/${firstRoundNum}`, '_blank');
      }
    }
  };

  return (<>
    <Clock
      currentTick={currentTick || 0}
      bombPlantTick={activeRoundData?.bombPlantTick || undefined}
      startTick={0}
      csVersion={csVersion}
      areaClock={false}
      area={undefined}
    />
    <div className="main" style={{ bottom: '53px' }}>
      <div className={styles.menuSection}>
        <AnalyseInspector
          progress={progress}
          playlistLength={playlistLength}
          roundsData={roundsData}
          playlist={playlist}
          teams={teams}
          currentAnalysisType={currentAnalysisType}
          setCurrentAnalysisType={setCurrentAnalysisType}
          selectOpponentTeam={selectOpponentTeam}
          setSelectOpponentTeam={setSelectOpponentTeam}
          currentSides={currentSides}
          setCurrentSides={setCurrentSides}
          currentEconomyType={currentEconomyType}
          setCurrentEconomyType={setCurrentEconomyType}
          currentHeroBuyTypes={currentHeroBuyTypes}
          setCurrentHeroBuyTypes={setCurrentHeroBuyTypes}
          currentOpponentBuyTypes={currentOpponentBuyTypes}
          setCurrentOpponentBuyTypes={setCurrentOpponentBuyTypes}
          currentHeroAWPType={currentHeroAWPType}
          setCurrentHeroAWPType={setCurrentHeroAWPType}
          currentOpponentAWPType={currentOpponentAWPType}
          setCurrentOpponentAWPType={setCurrentOpponentAWPType}
          currentVersusTypes={currentVersusTypes}
          setCurrentVersusTypes={setCurrentVersusTypes}
          currentBombsites={currentBombsites}
          setCurrentBombsites={setCurrentBombsites}
          currentPlayers={currentPlayers}
          setCurrentPlayers={setCurrentPlayers}
          currentVisibility={currentVisibility}
          setCurrentVisibility={setCurrentVisibility}
          heatmapSmoothing={heatmapSmoothing}
          setHeatmapSmoothing={setHeatmapSmoothing}
          filteredRoundCount={filteredRoundCount}
          players={players}
          playerColors={playerColors}
          resetActiveRound={resetActiveRound}
          resetSelectedProjectiles={resetSelectedProjectiles}
          setAutoPlay={setAutoPlay}
          onSelectMatchesClick={onSelectMatchesClick}
          visibilityOptions={visibilityOptions}
        />
      </div>
      <div className={styles.mapSection} onClick={activeRound !== undefined ? () => setSelectedRound(undefined) : undefined}>
        {currentAnalysisType[0] === 'heatmap' &&
          <AnalyseHeatMapView
            upperView={upperView}
            currentTick={currentTick}
            currentTickRange={currentTickRange}
            mapName={'de_' + playlist.mapname}
            playerFrames={playerFrames}
            projectiles={projectiles}
            smokes={smokes}
            fires={fires}
            explosions={explosions}
            smoothing={heatmapSmoothing}
          />
        }
        {currentAnalysisType[0] === 'replay' &&
          <AnalyseMapView
            upperView={upperView}
            currentTick={currentTick}
            mapName={'de_' + playlist.mapname}
            playerFrames={playerFrames}
            projectiles={projectiles}
            smokes={smokes}
            fires={fires}
            explosions={explosions}
            weaponFires={weaponFires}
            bullets={bullets}
            damages={damages}
            kills={Array.from(kills.values())}
            deaths={Array.from(deaths.values())}
            bomb={bomb}
            bombPlanted={bombPlanted}
            bombTimeLeft={bombTimeLeft}
            bombDefusing={bombDefusing}
            hoveredPlayerSteamId={hoveredPlayerSteamId}
            hoveredRound={hoveredRound}
            activePlayerSteamId={activePlayerSteamId}
            activeRound={activeRound}
            playerColors={playerColors}
            csVersion={csVersion}
            onPlayerBeginHover={onPlayerBeginHover}
            onPlayerEndHover={onPlayerEndHover}
            onPlayerClick={onPlayerClick}
            rect={marqueeRect}
            rectChanged={(
              recordingRect: number[] | undefined,
              rectType: 'all' | 'landing' | undefined,
              dragStart: boolean,
              dragEnd: boolean,
            ) => {
              OnRectSelectRounds(
                recordingRect,
                rectType,
                dragStart,
                dragEnd,
                playerFrames,
              )
            }}
            rectType={rectType}
          />
        }
        {currentAnalysisType[0] === 'grenades' &&
          <AnalyseGrenadesMapView
            upperView={upperView}
            currentTick={currentTick}
            currentTickRange={currentTickRange}
            mapName={'de_' + playlist.mapname}
            playerFrames={playerFrames}
            players={Object.keys(players).map(key => parseInt(key, 10))}
            projectiles={projectiles}
            projectileCache={getFilteredProjectileCache(undefined, filteredRounds)}
            selectedProjectileCache={selectedProjectiles}
            hoveredProjectile={hoveredProjectile}
            smokes={smokes}
            fires={fires}
            explosions={explosions}
            rect={marqueeRect}
            rectChanged={(
              recordingRect: number[] | undefined,
              rectType: 'all' | 'landing' | undefined,
              dragStart: boolean,
              dragEnd: boolean,
            ) => {
              OnRectSelectGrenades(
                recordingRect,
                rectType,
                dragStart,
                dragEnd,
                players,
                filteredRounds,
              )
            }}
            rectType={rectType}
          />
        }
      </div>
      <div className={styles.menuSection}>
        {activeRound !== undefined && activeRoundData !== undefined && selectedRoundType === "default" && activeRoundMetadata !== undefined && <>
          <div className="info w-100" style={{ margin: '0px', paddingBottom: '0px' }}>
            Round {zeroPad(activeRoundData.roundNum, 2)}<span style={{ color: '#555', paddingLeft: '10px' }}>{activeRoundMetadata.match_id.split('.')[0]}</span>
          </div>
          <AnalyseTeam
            roundData={activeRoundData}
            currentTick={currentTick}
            side={team_names_equal(playlist.team_key, activeRoundData.ctTeam) ? Side.CT : Side.T}
            teamKey={team_names_equal(playlist.team_key, activeRoundData.ctTeam) ? activeRoundMetadata.ct_team_key : activeRoundMetadata.t_team_key}
            teamName={team_names_equal(playlist.team_key, activeRoundData.ctTeam) ? activeRoundMetadata.ct_team : activeRoundMetadata.t_team}
            flag={team_names_equal(playlist.team_key, activeRoundData.ctTeam) ? activeRoundMetadata.ct_team_flag : activeRoundMetadata.t_team_flag}
          />
          <AnalyseTeam
            roundData={activeRoundData}
            currentTick={currentTick}
            side={team_names_equal(playlist.team_key, activeRoundData.tTeam) ? Side.CT : Side.T}
            teamKey={team_names_equal(playlist.team_key, activeRoundData.tTeam) ? activeRoundMetadata.ct_team_key : activeRoundMetadata.t_team_key}
            teamName={team_names_equal(playlist.team_key, activeRoundData.tTeam) ? activeRoundMetadata.ct_team : activeRoundMetadata.t_team}
            flag={team_names_equal(playlist.team_key, activeRoundData.tTeam) ? activeRoundMetadata.ct_team_flag : activeRoundMetadata.t_team_flag}
          />
          {selectedRound !== undefined &&
            <div className="info w-100" style={{ margin: '0px', paddingBottom: '0px' }}>
              <Button variant="outline-secondary" className="w-100" onClick={() => handleSelectedRoundBackClick()}><i className="bi bi-arrow-left" style={{ marginRight: '10px' }} />Back</Button>
            </div>
          }
        </>}
        {(currentAnalysisType[0] === 'replay' || currentAnalysisType[0] === 'heatmap') && (!activeRound || activeRound && selectedRoundType === "hover") &&
          <>
            {(selectedRounds === undefined || selectedRounds.size === 0) && (toBeSelectedRounds === undefined || toBeSelectedRounds.size === 0) && selectedProjectiles === undefined && <div className={styles.infoTableWrapper}><table className={styles.infoTable}>
              <tr>
                <td className={styles.infoTableHotkey}>Click player</td>
                <td className={styles.infoTableHotkeyDescription}>Inspect round</td>
              </tr>
              <tr>
                <td className={styles.infoTableHotkey}>Drag</td>
                <td className={styles.infoTableHotkeyDescription}>Select rounds</td>
              </tr>
              <tr>
                <td className={styles.infoTableHotkey}>L</td>
                <td className={styles.infoTableHotkeyDescription}>Toggle upper/lower map</td>
              </tr>
            </table></div>
            }
            
            {(selectedRounds !== undefined && selectedRounds.size > 0 || toBeSelectedRounds !== undefined && toBeSelectedRounds.size > 0) && 
              <AnalyseRoundsList
                selectedRounds={selectedRoundsList}
                invertSelectedRounds={invertSelectedRounds}
                currentPlaylist={playlist}
                totalRoundCount={filteredRoundCount}
                mapName={playlist.mapname || ''}
                onRemoveRound={(roundId: string | undefined) => handleRoundRemove(roundId || '')}
                onHoverRound={(roundId: string | undefined) => handleRoundHover(roundId)}
                onClickRound={(roundId: string | undefined) => handleRoundClick(roundId || '')}
                onInvertToggle={() => setInvertSelectedRounds(!invertSelectedRounds)}
                csVersion={csVersion}
              />
            }
            
            {!(selectedRounds !== undefined && selectedRounds.size > 0 || toBeSelectedRounds !== undefined && toBeSelectedRounds.size > 0) && 
             filteredRounds.length > 0 && 
              <div className={styles.replayAllContainer}>
                <div className={styles.replayButtonWrapper}>
                  <Button
                    style={{
                      position: "relative",
                      height: 38,
                      paddingLeft: '20px',
                      paddingRight: '26px',
                    }}
                    className="btn-sm"
                    onClick={(e) => handleReplayAllFilteredRounds(e)}
                  >
                    <i className="bi bi-play-fill" style={{ marginRight: '4px' }}></i>Replay all {<span style={{ color: '#99c' }}>({filteredRounds.length})</span>}
                  </Button>
                </div>
              </div>
            }
          </>
        }
        {currentAnalysisType[0] === 'grenades' &&
          <>
            {selectedProjectiles === undefined && <div className={styles.infoTableWrapper}><table className={styles.infoTable}>
              <tr>
                <td className={styles.infoTableHotkey}>Drag</td>
                <td className={styles.infoTableHotkeyDescription}>Select</td>
              </tr>
              <tr>
                <td className={styles.infoTableHotkey}>ALT <span style={{ color: '#777' }}>+</span> Drag</td>
                <td className={styles.infoTableHotkeyDescription}>Select landings only</td>
              </tr>
              {/* <tr>
                <td className={styles.infoTableHotkey} style={{ paddingTop: '20px' }}>SHIFT <span style={{ color: '#777' }}>+</span> Drag</td>
                <td className={styles.infoTableHotkeyDescription} style={{ paddingTop: '20px' }}>Add <span style={{ color: '#555' }}>/</span> remove</td>
              </tr>
              <tr>
                <td className={styles.infoTableHotkey}>SHIFT <span style={{ color: '#777' }}>+</span> ALT <span style={{ color: '#777' }}>+</span> Drag</td>
                <td className={styles.infoTableHotkeyDescription}>Add <span style={{ color: '#555' }}>/</span> remove</td>
              </tr> */}
            </table></div>
            }
            <AnalyseProjectilesList
              selectedProjectiles={selectedProjectiles}
              currentPlaylist={playlist}
              totalRoundCount={filteredRoundCount}
              mapName={playlist.mapname || ''}
              onRemoveProjectile={handleRemoveProjectile}
              onHoverProjectile={handleHoverProjectile}
              onClickProjectile={handleProjectileClick}
              csVersion={csVersion}
            />
          </>
        }
        {/* <>
          <div className={styles.betaTest}>
            This is a beta preview.<br />Would ❤️ to hear your <a href="https://discord.gg/kCTSbPZe7Q" target="_blank">feedback</a>.
          </div>
          <hr />
          <div className={styles.infoLabel}>
            Click on a player to inspect the round
          </div>
        </> */}

      </div>
    </div>
    <div className={styles.footer}>
      <div className={`${styles.footerWrapper} px-3`}>
        <div className="w-100 py-2">
          <AnalyseTimeline
            currentTick={currentTick}
            currentTickRange={currentTickRange}
            setCurrentTick={(tick: number) => {
              setCurrentTick(tick);
            }}
            setCurrentTickRange={(tickRange: number[]) => {
              setCurrentTickRange(tickRange);
            }}
            bombPlantedTick={activeRoundData?.bombPlantTick || undefined}
            csVersion={csVersion}
            endTick={maxTick}
            playbackSpeed={playbackSpeed}
            autoPlay={autoPlay}
            type={currentAnalysisType[0] === 'replay' ? 'default' : 'range'}
            setAutoPlay={(a) => {
              setAutoPlay(a)
            }}
            togglePlaybackSpeed={() => togglePlaybackSpeed(true)}
          />
        </div>
      </div>
    </div>
  </>);
}

export default Analyse;
